import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, FlaskConical, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { PatientSearchDropdown } from '@/components/SearchDropdowns';

const TEST_TEMPLATES = {
  'Complete Blood Count (CBC)': {
    category: 'blood',
    fields: {
      'WBC': { unit: '10^9/L', normal: '4.5-11.0' },
      'RBC': { unit: '10^12/L', normal: '4.5-5.5' },
      'Hemoglobin': { unit: 'g/dL', normal: '13.5-17.5' },
      'Hematocrit': { unit: '%', normal: '38.5-50' },
      'Platelets': { unit: '10^9/L', normal: '150-400' }
    }
  },
  'Basic Metabolic Panel (BMP)': {
    category: 'blood',
    fields: {
      'Glucose': { unit: 'mg/dL', normal: '70-100' },
      'Sodium': { unit: 'mmol/L', normal: '136-145' },
      'Potassium': { unit: 'mmol/L', normal: '3.5-5.0' },
      'Chloride': { unit: 'mmol/L', normal: '98-107' },
      'CO2': { unit: 'mmol/L', normal: '23-29' },
      'BUN': { unit: 'mg/dL', normal: '7-20' },
      'Creatinine': { unit: 'mg/dL', normal: '0.7-1.3' }
    }
  },
  'Lipid Panel': {
    category: 'blood',
    fields: {
      'Total Cholesterol': { unit: 'mg/dL', normal: '<200' },
      'LDL': { unit: 'mg/dL', normal: '<100' },
      'HDL': { unit: 'mg/dL', normal: '>40' },
      'Triglycerides': { unit: 'mg/dL', normal: '<150' }
    }
  },
  'Urinalysis': {
    category: 'urine',
    fields: {
      'Color': { unit: '', normal: 'Yellow' },
      'Clarity': { unit: '', normal: 'Clear' },
      'pH': { unit: '', normal: '4.5-8.0' },
      'Specific Gravity': { unit: '', normal: '1.005-1.030' },
      'Protein': { unit: '', normal: 'Negative' },
      'Glucose': { unit: '', normal: 'Negative' },
      'Ketones': { unit: '', normal: 'Negative' }
    }
  },
  'Chest X-Ray': {
    category: 'imaging',
    fields: {
      'Findings': { unit: '', normal: '' },
      'Impression': { unit: '', normal: '' }
    }
  }
};

export default function LabEntryForm() {
  const navigate = useNavigate();
  const { entryId } = useParams();
  const [searchParams] = useSearchParams();
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [visits, setVisits] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));
  
  const [formData, setFormData] = useState({
    visit_id: searchParams.get('visit_id') || '',
    patient_id: searchParams.get('patient_id') || '',
    test_name: '',
    test_category: 'blood',
    ordered_by: user.id,
    specimen_type: '',
    priority: 'routine',
    notes: ''
  });

  const [results, setResults] = useState({});
  const [status, setStatus] = useState('pending');
  const [interpretation, setInterpretation] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (entryId) {
      setIsEdit(true);
      fetchLabEntry();
    }
  }, [entryId]);

  useEffect(() => {
    // If visit_id is provided, fetch visit details
    if (searchParams.get('visit_id')) {
      fetchVisitDetails(searchParams.get('visit_id'));
    }
  }, [searchParams]);

  const fetchInitialData = async () => {
    try {
      const [patientsRes, doctorsRes, visitsRes] = await Promise.all([
        api.get('/patients'),
        api.get('/users/doctors'),
        api.get('/visits')  // Get all visits, not just in_consultation
      ]);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      // Sort visits with active ones first
      const activeStatuses = ['in_progress', 'in_consultation', 'checked_in'];
      const sortedVisits = [...visitsRes.data].sort((a, b) => {
        const aActive = activeStatuses.includes(a.status) ? 0 : 1;
        const bActive = activeStatuses.includes(b.status) ? 0 : 1;
        return aActive - bActive;
      });
      setVisits(sortedVisits);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const fetchVisitDetails = async (visitId) => {
    try {
      const response = await api.get(`/visits/${visitId}`);
      setSelectedVisit(response.data);
    } catch (error) {
      console.error('Failed to fetch visit details');
    }
  };

  const handleVisitChange = async (visitId) => {
    if (visitId && visitId !== 'none') {
      setFormData(prev => ({ ...prev, visit_id: visitId }));
      const visit = visits.find(v => v.id === visitId);
      if (visit) {
        setFormData(prev => ({ ...prev, patient_id: visit.patient_id, visit_id: visitId }));
        setSelectedVisit(visit);
      }
    } else {
      // Clear visit but keep patient selection
      setSelectedVisit(null);
      setFormData(prev => ({ ...prev, visit_id: '' }));
    }
  };

  const handlePatientChange = (patientId) => {
    setFormData(prev => ({ ...prev, patient_id: patientId, visit_id: '' }));
    setSelectedVisit(null);
  };

  const fetchLabEntry = async () => {
    try {
      const response = await api.get(`/lab-entries/${entryId}`);
      const entry = response.data;
      setFormData({
        visit_id: entry.visit_id || '',
        patient_id: entry.patient_id,
        test_name: entry.test_name,
        test_category: entry.test_category,
        ordered_by: entry.ordered_by,
        specimen_type: entry.specimen_type || '',
        priority: entry.priority,
        notes: entry.notes || ''
      });
      setResults(entry.results || {});
      setStatus(entry.status);
      setInterpretation(entry.interpretation || '');
      
      // Fetch visit details if visit_id exists
      if (entry.visit_id) {
        try {
          const visitRes = await api.get(`/visits/${entry.visit_id}`);
          setSelectedVisit(visitRes.data);
        } catch (e) {
          console.log('Could not fetch visit details');
        }
      }
    } catch (error) {
      toast.error('Failed to fetch lab entry');
    }
  };

  const handleTestNameChange = (testName) => {
    setFormData({ ...formData, test_name: testName });
    
    // If it's a template test, initialize results structure
    if (TEST_TEMPLATES[testName]) {
      const template = TEST_TEMPLATES[testName];
      setFormData(prev => ({ ...prev, test_category: template.category }));
      
      const initialResults = {};
      Object.keys(template.fields).forEach(field => {
        initialResults[field] = '';
      });
      setResults(initialResults);
    }
  };

  const handleResultChange = (field, value) => {
    setResults({ ...results, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate patient_id is present for new entries (visit is now optional)
    if (!isEdit && !formData.patient_id) {
      toast.error('Please select a patient');
      return;
    }
    
    setLoading(true);

    try {
      if (isEdit) {
        // Update existing entry
        await api.put(`/lab-entries/${entryId}`, {
          results,
          status,
          interpretation,
          performed_by: user.id,
          verified_by: status === 'verified' ? user.id : null
        });
        toast.success('Lab entry updated successfully');
        navigate('/lab-entries');
      } else {
        // Create new entry
        await api.post('/lab-entries', formData);
        toast.success('Lab order created successfully');
        
        // If came from visit details, navigate back there
        if (searchParams.get('visit_id')) {
          navigate(`/visits/${searchParams.get('visit_id')}`);
        } else {
          navigate('/lab-entries');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save lab entry');
    } finally {
      setLoading(false);
    }
  };

  const currentTemplate = TEST_TEMPLATES[formData.test_name];

  return (
    <div data-testid="lab-entry-form-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => searchParams.get('visit_id') ? navigate(`/visits/${searchParams.get('visit_id')}`) : navigate('/lab-entries')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {searchParams.get('visit_id') ? 'Back to Visit' : 'Back to Lab Dashboard'}
          </Button>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            {isEdit ? 'Update Lab Results' : 'Create Lab Order'}
          </h1>
          <p className="text-slate-600 mt-2">
            {isEdit ? 'Enter lab results and update status' : 'Order a new laboratory test'}
            {selectedVisit && (
              <span className="ml-2 font-medium">
                • Visit: {selectedVisit.visit_number} - {selectedVisit.patient?.full_name}
              </span>
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Order Information (only for new orders) */}
          {!isEdit && (
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-[#4A238F]" />
                  Order Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Patient Selection (Required) */}
                <div className="space-y-2">
                  <Label>Select Patient *</Label>
                  <PatientSearchDropdown
                    patients={patients}
                    selectedPatientId={formData.patient_id}
                    onSelect={handlePatientChange}
                    placeholder="Search patient by name, ID, or phone..."
                    required
                    disabled={!!searchParams.get('patient_id')}
                  />
                  {formData.patient_id && patients.find(p => p.id === formData.patient_id) && (
                    <p className="text-sm text-slate-500">
                      Selected: {patients.find(p => p.id === formData.patient_id)?.full_name} • 
                      {patients.find(p => p.id === formData.patient_id)?.patient_number}
                    </p>
                  )}
                </div>

                {/* Visit Selection (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="visit_id">Link to Visit (Optional)</Label>
                  {formData.patient_id ? (
                    <Select
                      value={formData.visit_id || 'none'}
                      onValueChange={handleVisitChange}
                      disabled={!!searchParams.get('visit_id')}
                    >
                      <SelectTrigger data-testid="visit-select">
                        <SelectValue placeholder="Select a visit (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No visit - standalone lab order</SelectItem>
                        {visits
                          .filter(v => v.patient_id === formData.patient_id)
                          .map((visit) => (
                            <SelectItem key={visit.id} value={visit.id}>
                              {visit.visit_number} - {visit.visit_type} - {visit.status}
                              {['in_progress', 'in_consultation', 'checked_in'].includes(visit.status) && ' ✓'}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-500">
                      Select a patient first to see their visits
                    </div>
                  )}
                  {selectedVisit && (
                    <p className="text-sm text-slate-500">
                      Visit: {selectedVisit.visit_number} • Complaint: {selectedVisit.chief_complaint || 'N/A'}
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <div className="space-y-2">
                    <Label htmlFor="test_name">Test Name *</Label>
                    <Select
                      value={formData.test_name}
                      onValueChange={handleTestNameChange}
                      required
                    >
                      <SelectTrigger data-testid="test-name-select">
                        <SelectValue placeholder="Select test" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(TEST_TEMPLATES).map((testName) => (
                          <SelectItem key={testName} value={testName}>
                            {testName}
                          </SelectItem>
                        ))}
                        <SelectItem value="Other">Other (Custom)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="test_category">Category *</Label>
                    <Select
                      value={formData.test_category}
                      onValueChange={(value) => setFormData({ ...formData, test_category: value })}
                      required
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blood">Blood</SelectItem>
                        <SelectItem value="urine">Urine</SelectItem>
                        <SelectItem value="imaging">Imaging</SelectItem>
                        <SelectItem value="pathology">Pathology</SelectItem>
                        <SelectItem value="microbiology">Microbiology</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority *</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) => setFormData({ ...formData, priority: value })}
                      required
                    >
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="routine">Routine</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="stat">STAT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="specimen_type">Specimen Type</Label>
                    <Input
                      id="specimen_type"
                      value={formData.specimen_type}
                      onChange={(e) => setFormData({ ...formData, specimen_type: e.target.value })}
                      placeholder="e.g., Blood, Serum, Urine"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ordered_by">Ordered By *</Label>
                    <Select
                      value={formData.ordered_by}
                      onValueChange={(value) => setFormData({ ...formData, ordered_by: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {doctors.map((doctor) => (
                          <SelectItem key={doctor.id} value={doctor.id}>
                            Dr. {doctor.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Clinical Notes</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                    placeholder="Clinical indications, relevant history..."
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Entry Info Display (for editing) */}
          {isEdit && (
            <Card className="border border-slate-200 shadow-sm bg-slate-50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-[#4A238F]" />
                  Lab Order Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Test Name</p>
                    <p className="text-slate-900 font-semibold">{formData.test_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Category</p>
                    <p className="text-slate-900 capitalize">{formData.test_category}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-600">Priority</p>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      formData.priority === 'stat' ? 'bg-rose-100 text-rose-700' :
                      formData.priority === 'urgent' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {formData.priority?.toUpperCase()}
                    </span>
                  </div>
                  {selectedVisit && (
                    <div>
                      <p className="text-sm font-medium text-slate-600">Visit</p>
                      <p className="text-slate-900">{selectedVisit.visit_number}</p>
                    </div>
                  )}
                  {selectedVisit?.patient && (
                    <div>
                      <p className="text-sm font-medium text-slate-600">Patient</p>
                      <p className="text-slate-900">{selectedVisit.patient?.full_name}</p>
                    </div>
                  )}
                  {formData.specimen_type && (
                    <div>
                      <p className="text-sm font-medium text-slate-600">Specimen Type</p>
                      <p className="text-slate-900">{formData.specimen_type}</p>
                    </div>
                  )}
                </div>
                {formData.notes && (
                  <div>
                    <p className="text-sm font-medium text-slate-600">Clinical Notes</p>
                    <p className="text-slate-900">{formData.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Results Entry (for editing) */}
          {isEdit && (
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Lab Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {currentTemplate ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(currentTemplate.fields).map(([field, info]) => (
                      <div key={field} className="space-y-2">
                        <Label htmlFor={field}>
                          {field} {info.unit && `(${info.unit})`}
                          {info.normal && <span className="text-xs text-slate-500 ml-2">Normal: {info.normal}</span>}
                        </Label>
                        <Input
                          id={field}
                          value={results[field] || ''}
                          onChange={(e) => handleResultChange(field, e.target.value)}
                          placeholder={`Enter ${field.toLowerCase()}`}
                          data-testid={`result-${field.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">Enter results as key-value pairs:</p>
                    {Object.keys(results).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(results).map(([field, value]) => (
                          <div key={field} className="space-y-2">
                            <Label htmlFor={field}>{field}</Label>
                            <Input
                              id={field}
                              value={value || ''}
                              onChange={(e) => handleResultChange(field, e.target.value)}
                              placeholder={`Enter ${field.toLowerCase()}`}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Parameter name"
                            id="new-result-key"
                          />
                          <Input
                            placeholder="Value"
                            id="new-result-value"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const key = document.getElementById('new-result-key').value;
                              const value = document.getElementById('new-result-value').value;
                              if (key) {
                                setResults(prev => ({ ...prev, [key]: value }));
                                document.getElementById('new-result-key').value = '';
                                document.getElementById('new-result-value').value = '';
                              }
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="interpretation">Clinical Interpretation</Label>
                  <textarea
                    id="interpretation"
                    value={interpretation}
                    onChange={(e) => setInterpretation(e.target.value)}
                    className="w-full min-h-[100px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                    placeholder="Interpretation of results, clinical significance..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status *</Label>
                  <Select value={status} onValueChange={setStatus} required>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit Button */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/lab-entries')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#0f766e] hover:bg-[#115e59] text-white"
              data-testid="submit-lab-entry-button"
            >
              <Save className="mr-2 h-4 w-4" />
              {loading ? 'Saving...' : isEdit ? 'Update Results' : 'Create Order'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
